import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { LookupItem } from '../../store/api/saleContractApi'
import {
  type SalePTBFFixation,
  type SalePTBFFixationPayload,
  useCreateSalePTBFFixationMutation,
  useGetSalePTBFFixationLotToFixQuery,
  useGetSalePTBFFixationQuery,
  useLazyGetSalePTBFFixationContractsQuery,
  useUpdateSalePTBFFixationEditOptionsMutation,
} from '../../store/api/salePTBFFixationsApi'

interface SalePTBFFixationFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  contractNo?: string | null
  onClose: () => void
}

interface SalePTBFFixationFormModel {
  contractNo: string
  dateFix: string
  releaseDate: string
  lotsFixed: number | ''
  levelFixed: number | ''
  isUpload: boolean
  isCusSign: boolean
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): SalePTBFFixationFormModel => ({
  contractNo: '',
  dateFix: today(),
  releaseDate: today(),
  lotsFixed: '',
  levelFixed: '',
  isUpload: false,
  isCusSign: false,
})

const safeDate = (value: unknown): string => {
  if (!value) return ''
  const text = String(value)
  return text.length >= 10 ? text.slice(0, 10) : text
}

const toNum = (value: number | ''): number | null => (value === '' ? null : value)

const apiToForm = (row: SalePTBFFixation): SalePTBFFixationFormModel => ({
  contractNo: row.contractNo ?? '',
  dateFix: safeDate(row.dateFix) || today(),
  releaseDate: safeDate(row.releaseDate) || today(),
  lotsFixed: row.lotsFixed ?? '',
  levelFixed: row.levelFixed ?? '',
  isUpload: Boolean(row.isUpload),
  isCusSign: Boolean(row.isCusSign),
})

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save PTBF Fixation. Please try again.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save PTBF Fixation. Please try again.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save PTBF Fixation. Please try again.'
}

const roSx = { '& .MuiInputBase-input': { color: 'text.secondary' } }

export default function SalePTBFFixationFormDialog({ open, mode, contractNo, onClose }: SalePTBFFixationFormDialogProps) {
  const [form, setForm] = useState<SalePTBFFixationFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fetchContracts, { data: contractOptions, isFetching: contractsLoading }] =
    useLazyGetSalePTBFFixationContractsQuery()
  const { data: editData, isFetching: editLoading } = useGetSalePTBFFixationQuery(contractNo ?? '', {
    skip: !open || mode !== 'edit' || !contractNo,
  })
  const { data: lotToFixData } = useGetSalePTBFFixationLotToFixQuery(form.contractNo, {
    skip: !open || !form.contractNo,
  })
  const [createFixation, { isLoading: creating }] = useCreateSalePTBFFixationMutation()
  const [updateEditOptions, { isLoading: updating }] = useUpdateSalePTBFFixationEditOptionsMutation()

  const contractItems = useMemo(() => contractOptions?.items ?? [], [contractOptions])
  const lotToFix = lotToFixData?.lotToFix ?? lotToFixData?.balanceLotsTbf ?? null
  const readOnlyContext = useMemo(
    () => ({
      contractSale: editData?.contractSale ?? '',
      buyersRef: editData?.buyersRef ?? '',
      cpCode: editData?.cpCode ?? '',
      clientName: editData?.name ?? '',
      quality: editData?.quality ?? '',
      quantity: editData?.quantity ?? '',
      mtons: editData?.mtons ?? '',
      noOfLots: editData?.noOfLots ?? '',
      ptbfDiffs: editData?.ptbfDiffs ?? '',
      futuresMonth: editData?.futuresMonth ?? '',
      balanceLotsTbf: editData?.balanceLotsTbf ?? lotToFix ?? '',
      finalPrice: editData?.finalPrice ?? '',
      fxRoe: editData?.fxRoe ?? '',
      qualityDescriptionOffshore: editData?.qualityDescriptionOffshore ?? '',
      qualityDescriptionContract: editData?.qualityDescriptionContract ?? '',
      dateUpdate: editData?.dateUpdate ?? '',
      personUpdate: editData?.personUpdate ?? '',
    }),
    [editData, lotToFix],
  )

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    if (mode === 'new') {
      setForm(emptyForm())
      fetchContracts('')
    }
  }, [open, mode, fetchContracts])

  useEffect(() => {
    if (open && mode === 'edit' && editData) {
      setForm(apiToForm(editData))
    }
  }, [open, mode, editData])

  useEffect(() => {
    if (mode === 'new' && form.contractNo && !form.releaseDate) {
      setForm((prev) => ({ ...prev, releaseDate: today() }))
    }
  }, [form.contractNo, form.releaseDate, mode])

  const set = <K extends keyof SalePTBFFixationFormModel>(key: K, value: SalePTBFFixationFormModel[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const setNum = (key: keyof SalePTBFFixationFormModel, raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === '' ? '' : Number(raw) }))

  const validateLikeAccess = (): string | null => {
    if (!form.contractNo.trim()) return 'Please choose Contract No!'
    if (!form.dateFix) return 'Please choose Date Fixed!'
    if (form.lotsFixed === '') return 'Please type Lots Fixed!'
    if (form.levelFixed === '') return 'Please type Level Fixed!'
    if (lotToFix != null && Number(form.lotsFixed) > Number(lotToFix)) return "'Lots Fixed' must not be greater than 'Lot to fix'!"
    return null
  }

  const handleSave = async () => {
    const accessError = validateLikeAccess()
    if (accessError) {
      setValidationError(accessError)
      return
    }
    setValidationError(null)
    try {
      if (mode === 'new') {
        const body: SalePTBFFixationPayload = {
          contractNo: form.contractNo,
          dateFix: form.dateFix,
          releaseDate: form.releaseDate || today(),
          lotsFixed: toNum(form.lotsFixed),
          levelFixed: toNum(form.levelFixed),
          isUpload: form.isUpload ? 1 : 0,
          isCusSign: form.isCusSign ? 1 : 0,
        }
        await createFixation(body).unwrap()
      } else if (contractNo) {
        const body: SalePTBFFixationPayload = {
          contractNo: form.contractNo,
          releaseDate: form.releaseDate || today(),
          isUpload: form.isUpload ? 1 : 0,
          isCusSign: form.isCusSign ? 1 : 0,
        }
        await updateEditOptions({ contractNo, body }).unwrap()
      }
      onClose()
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const title = mode === 'new' ? 'Sale PTBF Fixation - Add' : 'Sale PTBF Fixation - Edit'

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={creating || updating || editLoading}>
            {creating || updating || editLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {validationError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {validationError}
          </Alert>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            {mode === 'new' ? (
              <Autocomplete
                size="small"
                options={contractItems}
                loading={contractsLoading}
                getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                inputValue={form.contractNo}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    set('contractNo', value)
                    fetchContracts(value)
                  } else if (reason === 'clear') {
                    set('contractNo', '')
                  }
                }}
                onChange={(_, item) => set('contractNo', item?.value ?? '')}
                filterOptions={(x) => x}
                renderInput={(params) => <TextField {...params} label="Contract no" />}
              />
            ) : (
              <TextField label="Contract no" size="small" fullWidth value={form.contractNo} InputProps={{ readOnly: true }} sx={roSx} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Date Fixed"
              type="date"
              size="small"
              fullWidth
              value={form.dateFix}
              onChange={(e) => set('dateFix', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: mode === 'edit' }}
              sx={mode === 'edit' ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Release Date"
              type="date"
              size="small"
              fullWidth
              value={form.releaseDate}
              onChange={(e) => set('releaseDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Lots Fixed"
              type="number"
              size="small"
              fullWidth
              value={form.lotsFixed}
              onChange={(e) => setNum('lotsFixed', e.target.value)}
              InputProps={{ readOnly: mode === 'edit' }}
              sx={mode === 'edit' ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Level Fixed"
              type="number"
              size="small"
              fullWidth
              value={form.levelFixed}
              onChange={(e) => setNum('levelFixed', e.target.value)}
              InputProps={{ readOnly: mode === 'edit' }}
              sx={mode === 'edit' ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Lot to Fix"
              size="small"
              fullWidth
              value={lotToFix ?? ''}
              InputProps={{ readOnly: true }}
              sx={roSx}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Contract context
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <TextField label="Contract Sale" size="small" fullWidth value={readOnlyContext.contractSale} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Buyer Ref" size="small" fullWidth value={readOnlyContext.buyersRef} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Client Code" size="small" fullWidth value={readOnlyContext.cpCode} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Client" size="small" fullWidth value={readOnlyContext.clientName} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Quality" size="small" fullWidth value={readOnlyContext.quality} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Quantity" size="small" fullWidth value={readOnlyContext.quantity} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Mtons" size="small" fullWidth value={readOnlyContext.mtons} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="No of Lots" size="small" fullWidth value={readOnlyContext.noOfLots} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="PTBF Diffs" size="small" fullWidth value={readOnlyContext.ptbfDiffs} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Futures Month" size="small" fullWidth value={readOnlyContext.futuresMonth} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Balance Lots TBF" size="small" fullWidth value={readOnlyContext.balanceLotsTbf} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Final Price" size="small" fullWidth value={readOnlyContext.finalPrice} InputProps={{ readOnly: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quality description offshore"
              size="small"
              fullWidth
              value={readOnlyContext.qualityDescriptionOffshore}
              InputProps={{ readOnly: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quality description contract"
              size="small"
              fullWidth
              value={readOnlyContext.qualityDescriptionContract}
              InputProps={{ readOnly: true }}
              sx={roSx}
            />
          </Grid>
        </Grid>

        {mode === 'edit' && (
          <>
            <Box sx={{ mt: 2, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                Edit options
              </Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Box>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={<Checkbox checked={form.isUpload} onChange={(e) => set('isUpload', e.target.checked)} />}
                  label="Upload"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={<Checkbox checked={form.isCusSign} onChange={(e) => set('isCusSign', e.target.checked)} />}
                  label="Customer signed"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Updated Date"
                  size="small"
                  fullWidth
                  value={readOnlyContext.dateUpdate}
                  InputProps={{ readOnly: true }}
                  sx={roSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Updated Person"
                  size="small"
                  fullWidth
                  value={readOnlyContext.personUpdate}
                  InputProps={{ readOnly: true }}
                  sx={roSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ROE"
                  size="small"
                  fullWidth
                  value={readOnlyContext.fxRoe}
                  InputProps={{ readOnly: true }}
                  sx={roSx}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
